import { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowRightToBracket, faArrowRightFromBracket, faCalendarPlus,
  faBan, faCreditCard, faBell, faPhone, faMoon,
  faCircleCheck, faTriangleExclamation, faRefresh,
  faCommentDots, faClock, faArrowUpRightFromSquare,
} from "@fortawesome/free-solid-svg-icons";
import DashboardLayout from "../layout/DashboardLayout";
import "../layout/Dashboard.css";
import "./NotificationsPage.css";

const API_BASE = import.meta.env.VITE_API_URL ?? "http://localhost:5050";

interface RoomRef { roomId: string; roomNumber: string; }

interface NotifBooking {
  id: string;
  bookingId: string;
  guestName: string;
  roomId: string;
  roomNumber: string;
  allRooms: RoomRef[];
  roomNumbers: string;
  phone?: string;
  checkIn: string | null;
  checkOut: string | null;
  nights: number;
  total: number;
  bookingType?: string;
  status?: string;
  createdAt: string | null;
  roomReady?: boolean;
}

interface ActivityEntry {
  id: number;
  type: string;
  bookingid?: string;
  guestname?: string;
  roomnumber?: string;
  message?: string;
  is_read?: boolean;
  created_at: string;
}

interface NotifData {
  todayArrivals: NotifBooking[];
  todayDepartures: NotifBooking[];
  upcomingArrivals: NotifBooking[];
  recentBookings: NotifBooking[];
  activityLog: ActivityEntry[];
  targetDate?: string;
}

type Tab = "Today" | "Upcoming" | "Activity";

function localToday() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
}

function fmt(d?: string | null) {
  if (!d) return "—";
  return new Date(d + "T12:00:00").toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
}

function fmtFull(d?: string | null) {
  if (!d) return "—";
  return new Date(d + "T12:00:00").toLocaleDateString("en-GB", { weekday: "short", day: "2-digit", month: "short", year: "numeric" });
}

function daysUntil(dateStr: string) {
  const now = new Date(localToday() + "T12:00:00");
  const target = new Date(dateStr + "T12:00:00");
  return Math.round((target.getTime() - now.getTime()) / 86400000);
}

function timeAgo(iso: string) {
  const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

function BookingTypeBadge({ type }: { type?: string }) {
  const m: Record<string, { color: string; bg: string }> = {
    Standard: { color: "#C9A84C", bg: "rgba(201,168,76,0.14)" },
    Booking:  { color: "#C9A84C", bg: "rgba(201,168,76,0.14)" },
    HouseUse: { color: "#34d399", bg: "rgba(52,211,153,0.14)" },
    Special:  { color: "#fbbf24", bg: "rgba(251,191,36,0.14)" },
  };
  const s = m[type || "Standard"] || m.Standard;
  return (
    <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 99, background: s.bg, color: s.color, border: `1px solid ${s.color}33`, letterSpacing: "0.02em" }}>
      {type || "Standard"}
    </span>
  );
}

function activityIcon(type: string) {
  switch (type) {
    case "checkin":
    case "check_in":           return { icon: faArrowRightToBracket,   color: "#10b981", bg: "rgba(16,185,129,0.1)",   label: "Checked In" };
    case "checkout":
    case "check_out":          return { icon: faArrowRightFromBracket, color: "#3b82f6", bg: "rgba(59,130,246,0.1)",   label: "Checked Out" };
    case "new_booking":        return { icon: faCalendarPlus,          color: "#c9a84c", bg: "rgba(201,168,76,0.1)",   label: "New Booking" };
    case "cancellation":       return { icon: faBan,                   color: "#f87171", bg: "rgba(248,113,113,0.1)",  label: "Cancelled" };
    case "payment":            return { icon: faCreditCard,            color: "#8b5cf6", bg: "rgba(139,92,246,0.1)",   label: "Payment" };
    case "whatsapp_booking":   return { icon: faCommentDots,           color: "#25D366", bg: "rgba(37,211,102,0.1)",   label: "WA Booking" };
    case "whatsapp_handoff":
    case "handoff":            return { icon: faTriangleExclamation,   color: "#f87171", bg: "rgba(248,113,113,0.1)",  label: "Handoff Alert" };
    case "whatsapp_off_hours":
    case "off_hours":          return { icon: faClock,                 color: "#a78bfa", bg: "rgba(167,139,250,0.1)",  label: "After Hours" };
    case "whatsapp_alert":     return { icon: faCommentDots,           color: "#25D366", bg: "rgba(37,211,102,0.1)",   label: "WA Alert" };
    case "booking_lead":       return { icon: faCalendarPlus,          color: "#c9a84c", bg: "rgba(201,168,76,0.1)",   label: "Booking Lead" };
    case "pre_checkin":        return { icon: faArrowRightToBracket,   color: "#c9a84c", bg: "rgba(201,168,76,0.1)",   label: "Pre Check-in" };
    case "housekeeping":       return { icon: faBell,                  color: "#f97316", bg: "rgba(249,115,22,0.1)",   label: "Housekeeping" };
    default:                   return { icon: faBell,                  color: "rgba(242,235,217,0.4)", bg: "rgba(100,116,139,0.1)", label: type.replace(/_/g, " ") };
  }
}

function RoomsList({ b }: { b: NotifBooking }) {
  const hasMulti = b.allRooms?.length > 1;
  if (hasMulti) {
    return <span>Rooms <strong>{b.roomNumbers}</strong></span>;
  }
  return <span>Room <strong>{b.roomNumber || b.roomId}</strong></span>;
}

/* ── Arrival card ─────────────────────────────────────────── */
function ArrivalCard({ b, onNavigate }: { b: NotifBooking; onNavigate: (id: string) => void }) {
  const isNoCharge = b.bookingType === "HouseUse" || b.bookingType === "Special";
  const days = b.checkIn ? daysUntil(b.checkIn) : null;
  const isToday = days === 0;
  return (
    <div className="np-card">
      <div className="np-card-main">
        <div className="np-card-avatar np-av-green">{b.guestName?.[0] ?? "?"}</div>
        <div className="np-card-body">
          <div className="np-card-name-row">
            <span className="np-card-name">{b.guestName}</span>
            <BookingTypeBadge type={b.bookingType} />
          </div>
          <div className="np-card-sub">
            <RoomsList b={b} />
            {b.phone && b.phone !== "—" && <><span className="np-dot" /> <FontAwesomeIcon icon={faPhone} style={{ fontSize: 10 }} /> {b.phone}</>}
          </div>
        </div>
        <div className="np-card-badge np-badge-arrive">
          {isToday ? "Today" : days !== null ? `In ${days}d` : "Arriving"}
        </div>
      </div>
      <div className="np-card-grid">
        <div className="np-meta-cell">
          <span className="np-meta-lbl">Check-in</span>
          <span className="np-meta-val">{fmtFull(b.checkIn)}</span>
        </div>
        <div className="np-meta-cell">
          <span className="np-meta-lbl">Check-out</span>
          <span className="np-meta-val">{fmt(b.checkOut)}</span>
        </div>
        <div className="np-meta-cell">
          <span className="np-meta-lbl"><FontAwesomeIcon icon={faMoon} /> Nights</span>
          <span className="np-meta-val">{b.nights}</span>
        </div>
        <div className="np-meta-cell">
          <span className="np-meta-lbl">Total</span>
          <span className="np-meta-val">{isNoCharge ? <span style={{ color: "#10b981" }}>No charge</span> : `EGP ${b.total?.toLocaleString() ?? "—"}`}</span>
        </div>
        {isToday && (
          <div className="np-meta-cell np-meta-span2">
            <span className="np-meta-lbl">Room</span>
            <span className="np-meta-val">
              {b.roomReady
                ? <span style={{ color: "#10b981" }}><FontAwesomeIcon icon={faCircleCheck} /> Ready</span>
                : <span style={{ color: "#f59e0b" }}><FontAwesomeIcon icon={faTriangleExclamation} /> Not Ready</span>}
            </span>
          </div>
        )}
      </div>
      <div className="np-card-actions">
        <button className="np-quick-link" onClick={() => onNavigate(b.bookingId)}>
          <FontAwesomeIcon icon={faArrowUpRightFromSquare} /> View Booking
        </button>
        <span className="np-booking-id">{b.bookingId}</span>
      </div>
    </div>
  );
}

/* ── Departure card ───────────────────────────────────────── */
function DepartureCard({ b, onNavigate }: { b: NotifBooking; onNavigate: (id: string) => void }) {
  const isNoCharge = b.bookingType === "HouseUse" || b.bookingType === "Special";
  return (
    <div className="np-card">
      <div className="np-card-main">
        <div className="np-card-avatar np-av-blue">{b.guestName?.[0] ?? "?"}</div>
        <div className="np-card-body">
          <div className="np-card-name-row">
            <span className="np-card-name">{b.guestName}</span>
            <BookingTypeBadge type={b.bookingType} />
          </div>
          <div className="np-card-sub">
            <RoomsList b={b} />
            {b.phone && b.phone !== "—" && <><span className="np-dot" /> <FontAwesomeIcon icon={faPhone} style={{ fontSize: 10 }} /> {b.phone}</>}
          </div>
        </div>
        <div className="np-card-badge np-badge-depart">Departing</div>
      </div>
      <div className="np-card-grid">
        <div className="np-meta-cell">
          <span className="np-meta-lbl">Checked In</span>
          <span className="np-meta-val">{fmtFull(b.checkIn)}</span>
        </div>
        <div className="np-meta-cell">
          <span className="np-meta-lbl">Check-out</span>
          <span className="np-meta-val">{fmt(b.checkOut)}</span>
        </div>
        <div className="np-meta-cell">
          <span className="np-meta-lbl"><FontAwesomeIcon icon={faMoon} /> Nights</span>
          <span className="np-meta-val">{b.nights}</span>
        </div>
        <div className="np-meta-cell">
          <span className="np-meta-lbl">Total</span>
          <span className="np-meta-val">{isNoCharge ? <span style={{ color: "#10b981" }}>No charge</span> : `EGP ${b.total?.toLocaleString() ?? "—"}`}</span>
        </div>
      </div>
      <div className="np-card-actions">
        <button className="np-quick-link" onClick={() => onNavigate(b.bookingId)}>
          <FontAwesomeIcon icon={faArrowUpRightFromSquare} /> View Booking
        </button>
        <span className="np-booking-id">{b.bookingId}</span>
      </div>
    </div>
  );
}

/* ── Main Page ────────────────────────────────────────────── */
export default function NotificationsPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<Tab>("Today");
  const [data, setData] = useState<NotifData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const token = localStorage.getItem("ai2_token");
  const headers = { Authorization: `Bearer ${token}` };

  const goBooking = (bookingId: string) => navigate(`/dashboard/manager/bookings?id=${bookingId}`);

  const load = () => {
    setLoading(true); setError("");
    axios.get(`${API_BASE}/api/dashboard/notifications`, { headers })
      .then(r => setData(r.data))
      .catch(() => setError("Failed to load notifications"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  // Group upcoming by date
  const groupedUpcoming = (data?.upcomingArrivals ?? []).reduce<Record<string, NotifBooking[]>>((acc, b) => {
    const k = b.checkIn ?? "Unknown";
    if (!acc[k]) acc[k] = [];
    acc[k].push(b);
    return acc;
  }, {});

  const totalToday = (data?.todayArrivals.length ?? 0) + (data?.todayDepartures.length ?? 0);

  return (
    <DashboardLayout>
      <div className="np-page">

        {/* Header */}
        <div className="np-header">
          <div>
            <h1 className="np-title">Notifications</h1>
            <p className="np-subtitle">{new Date().toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}</p>
          </div>
          <button className="np-refresh-btn" onClick={load} disabled={loading}>
            <FontAwesomeIcon icon={faRefresh} spin={loading} /> Refresh
          </button>
        </div>

        {/* Stats */}
        {data && (
          <div className="np-stats-row">
            <div className="np-stat np-stat-green">
              <span className="np-stat-num">{data.todayArrivals.length}</span>
              <span className="np-stat-lbl">Arrivals Today</span>
            </div>
            <div className="np-stat np-stat-blue">
              <span className="np-stat-num">{data.todayDepartures.length}</span>
              <span className="np-stat-lbl">Departures Today</span>
            </div>
            <div className="np-stat np-stat-gold">
              <span className="np-stat-num">{data.upcomingArrivals.length}</span>
              <span className="np-stat-lbl">Upcoming (7 days)</span>
            </div>
            <div className="np-stat np-stat-purple">
              <span className="np-stat-num">{data.recentBookings.length}</span>
              <span className="np-stat-lbl">New Bookings (7d)</span>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="np-tabs">
          {([
            { key: "Today" as Tab, count: totalToday },
            { key: "Upcoming" as Tab, count: data?.upcomingArrivals.length ?? 0 },
            { key: "Activity" as Tab, count: data?.activityLog.length ?? 0 },
          ]).map(({ key, count }) => (
            <button key={key} className={`np-tab ${activeTab === key ? "active" : ""}`} onClick={() => setActiveTab(key)}>
              {key}
              <span className={`np-tab-count ${activeTab === key ? "active" : ""}`}>{count}</span>
            </button>
          ))}
        </div>

        {loading && <div className="np-loading">Loading…</div>}
        {error && <div className="np-error">{error}</div>}

        {!loading && !error && data && (
          <>
            {/* ── TODAY ── */}
            {activeTab === "Today" && (
              <div className="np-two-col">

                {/* Arrivals */}
                <div className="np-section">
                  <div className="np-section-head">
                    <div className="np-section-icon np-icon-green"><FontAwesomeIcon icon={faArrowRightToBracket} /></div>
                    <div>
                      <h2 className="np-section-title">Checking In Today</h2>
                      <p className="np-section-sub">{data.todayArrivals.length} arrival{data.todayArrivals.length !== 1 ? "s" : ""}</p>
                    </div>
                    <span className="np-pill np-pill-green">{data.todayArrivals.length}</span>
                  </div>
                  {data.todayArrivals.length === 0
                    ? <div className="np-empty"><FontAwesomeIcon icon={faCircleCheck} /> All guests checked in</div>
                    : <div className="np-card-list">{data.todayArrivals.map(b => <ArrivalCard key={b.id} b={b} onNavigate={goBooking} />)}</div>
                  }
                </div>

                {/* Departures */}
                <div className="np-section">
                  <div className="np-section-head">
                    <div className="np-section-icon np-icon-blue"><FontAwesomeIcon icon={faArrowRightFromBracket} /></div>
                    <div>
                      <h2 className="np-section-title">Checking Out Today</h2>
                      <p className="np-section-sub">{data.todayDepartures.length} departure{data.todayDepartures.length !== 1 ? "s" : ""}</p>
                    </div>
                    <span className="np-pill np-pill-blue">{data.todayDepartures.length}</span>
                  </div>
                  {data.todayDepartures.length === 0
                    ? <div className="np-empty"><FontAwesomeIcon icon={faCircleCheck} /> No departures today</div>
                    : <div className="np-card-list">{data.todayDepartures.map(b => <DepartureCard key={b.id} b={b} onNavigate={goBooking} />)}</div>
                  }
                </div>
              </div>
            )}

            {/* ── UPCOMING ── */}
            {activeTab === "Upcoming" && (
              <div className="np-upcoming-layout">

                {/* 7-day arrivals */}
                <div className="np-section np-section-wide">
                  <div className="np-section-head">
                    <div className="np-section-icon np-icon-gold"><FontAwesomeIcon icon={faCalendarPlus} /></div>
                    <div>
                      <h2 className="np-section-title">Upcoming Arrivals — Next 7 Days</h2>
                      <p className="np-section-sub">{data.upcomingArrivals.length} confirmed booking{data.upcomingArrivals.length !== 1 ? "s" : ""}</p>
                    </div>
                    <span className="np-pill np-pill-gold">{data.upcomingArrivals.length}</span>
                  </div>
                  {data.upcomingArrivals.length === 0
                    ? <div className="np-empty">No upcoming arrivals in the next 7 days</div>
                    : (
                      <div className="np-grouped">
                        {Object.entries(groupedUpcoming).sort(([a], [b]) => a.localeCompare(b)).map(([dateStr, bookings]) => (
                          <div key={dateStr} className="np-date-group">
                            <div className="np-date-label">
                              <span>{new Date(dateStr + "T12:00:00").toLocaleDateString("en-GB", { weekday: "short", day: "2-digit", month: "short" })}</span>
                              <span className="np-date-in">{daysUntil(dateStr) === 1 ? "Tomorrow" : `in ${daysUntil(dateStr)} days`}</span>
                            </div>
                            <div className="np-card-list">
                              {bookings.map(b => <ArrivalCard key={b.id} b={b} onNavigate={goBooking} />)}
                            </div>
                          </div>
                        ))}
                      </div>
                    )
                  }
                </div>

                {/* Recent bookings */}
                <div className="np-section np-section-wide">
                  <div className="np-section-head">
                    <div className="np-section-icon np-icon-purple"><FontAwesomeIcon icon={faCalendarPlus} /></div>
                    <div>
                      <h2 className="np-section-title">Recently Added Bookings</h2>
                      <p className="np-section-sub">Last 7 days</p>
                    </div>
                    <span className="np-pill np-pill-purple">{data.recentBookings.length}</span>
                  </div>
                  {data.recentBookings.length === 0
                    ? <div className="np-empty">No new bookings in the last 7 days</div>
                    : (
                      <div className="np-card-list">
                        {data.recentBookings.map(b => (
                          <div key={b.id} className="np-card">
                            <div className="np-card-main">
                              <div className="np-card-avatar np-av-purple">{b.guestName?.[0] ?? "?"}</div>
                              <div className="np-card-body">
                                <div className="np-card-name-row">
                                  <span className="np-card-name">{b.guestName}</span>
                                  <BookingTypeBadge type={b.bookingType} />
                                </div>
                                <div className="np-card-sub"><RoomsList b={b} /></div>
                              </div>
                              <div style={{ textAlign: "right" }}>
                                <div className="np-card-badge np-badge-new">New</div>
                                <div style={{ fontSize: 11, color: "rgba(242,235,217,0.3)", marginTop: 4 }}>{b.createdAt ? timeAgo(b.createdAt) : ""}</div>
                              </div>
                            </div>
                            <div className="np-card-grid">
                              <div className="np-meta-cell">
                                <span className="np-meta-lbl">Check-in</span>
                                <span className="np-meta-val">{fmtFull(b.checkIn)}</span>
                              </div>
                              <div className="np-meta-cell">
                                <span className="np-meta-lbl">Check-out</span>
                                <span className="np-meta-val">{fmt(b.checkOut)}</span>
                              </div>
                              <div className="np-meta-cell">
                                <span className="np-meta-lbl"><FontAwesomeIcon icon={faMoon} /> Nights</span>
                                <span className="np-meta-val">{b.nights}</span>
                              </div>
                              <div className="np-meta-cell">
                                <span className="np-meta-lbl">Total</span>
                                <span className="np-meta-val">EGP {b.total?.toLocaleString() ?? "—"}</span>
                              </div>
                            </div>
                            <div className="np-card-actions">
                              <button className="np-quick-link" onClick={() => goBooking(b.bookingId)}>
                                <FontAwesomeIcon icon={faArrowUpRightFromSquare} /> View Booking
                              </button>
                              <span className="np-booking-id">{b.bookingId}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )
                  }
                </div>
              </div>
            )}

            {/* ── ACTIVITY ── */}
            {activeTab === "Activity" && (
              <div className="np-section np-section-wide">
                <div className="np-section-head">
                  <div className="np-section-icon" style={{ background: "rgba(100,116,139,0.1)", color: "rgba(242,235,217,0.4)" }}><FontAwesomeIcon icon={faBell} /></div>
                  <div>
                    <h2 className="np-section-title">Recent Activity</h2>
                    <p className="np-section-sub">Last 48 hours · {data.activityLog.length} event{data.activityLog.length !== 1 ? "s" : ""}</p>
                  </div>
                </div>
                {data.activityLog.length === 0
                  ? <div className="np-empty">No activity in the last 48 hours</div>
                  : (
                    <div className="np-activity-list">
                      {data.activityLog.map(entry => {
                        const { icon, color, bg, label } = activityIcon(entry.type);
                        return (
                          <div
                            key={entry.id}
                            className={`np-activity-row${entry.is_read ? "" : " np-activity-unread"}${entry.bookingid ? " np-activity-clickable" : ""}`}
                            onClick={() => entry.bookingid && goBooking(entry.bookingid)}
                          >
                            <div className="np-activity-icon" style={{ background: bg, color }}>
                              <FontAwesomeIcon icon={icon} />
                            </div>
                            <div className="np-activity-body">
                              <div className="np-activity-label-row">
                                <span className="np-activity-type-badge" style={{ background: bg, color }}>{label}</span>
                                {entry.guestname && <strong className="np-activity-guest">{entry.guestname}</strong>}
                                {entry.roomnumber && <span className="np-activity-room">Room {entry.roomnumber}</span>}
                              </div>
                              <p className="np-activity-msg">{entry.message || entry.type.replace(/_/g, " ")}</p>
                            </div>
                            <div className="np-activity-time">
                              {timeAgo(entry.created_at)}
                              {!entry.is_read && <span className="np-unread-dot" />}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )
                }
              </div>
            )}
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
